import { BottomNavigation, BottomNavigationAction, Paper } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { UserRole } from "../../auth/roles";
import { useVisibleMenu } from "./AppSidebar";
import { DASHBOARD_ROUTE, useActiveMenuRoute } from "./menu/activeRoute";
import { MenuItem } from "./menuConfig";
import { DashboardIcon, menuIconMap } from "./menu/menuIcons";

export const BOTTOM_NAV_HEIGHT = 64;

// Pages on the bar (besides Home and More), in order; each shows only if the role can open it.
// Students get their own fee pages, since Attendance and Notifications aren't theirs.
const STAFF_ROUTES = ["/app/attendance/students", "/app/notifications/send"];
const STUDENT_ROUTES = ["/app/fees/outstanding", "/app/fees/receipts"];

// bar icons by route (the group's icon reads better than the item's on a bottom bar)
const BAR_ICONS: Record<string, string> = {
  "/app/attendance/students": "attendance",
  "/app/notifications/send": "notifications",
  "/app/fees/outstanding": "outstanding",
  "/app/fees/receipts": "receipts",
};

const SHORT_LABELS: Record<string, string> = {
  "/app/attendance/students": "Attendance",
  "/app/students": "Students",
  "/app/fees/outstanding": "Dues",
  "/app/fees/receipts": "Receipts",
  "/app/fees/payments": "Payments",
  "/app/enquiries": "Enquiries",
  "/app/notifications/send": "Notifications",
};

/** Phone-only bottom bar: Home, Attendance, Notifications (students: Dues, Receipts), More (opens the full menu drawer). */
export const AppBottomNav = ({ onMore }: { onMore: () => void }) => {
  const navigate = useNavigate();
  const visible = useVisibleMenu();
  const { user } = useAuth();

  // each with its bar icon (BAR_ICONS), falling back to the group's icon
  const items: { item: MenuItem; icon: string }[] = useMemo(() => {
    const allowed = visible.flatMap((entry) =>
      entry.items
        .filter((item) => item.status === "active")
        .map((item) => ({ item, icon: BAR_ICONS[item.route] ?? entry.group.icon }))
    );
    const routes = user?.role === UserRole.STUDENT ? STUDENT_ROUTES : STAFF_ROUTES;
    return routes.map((route) => allowed.find((entry) => entry.item.route === route))
      .filter((entry): entry is { item: MenuItem; icon: string } => Boolean(entry))
      .slice(0, 3);
  }, [visible, user]);

  const activeRoute = useActiveMenuRoute();
  const current = activeRoute === DASHBOARD_ROUTE ? DASHBOARD_ROUTE : items.find((entry) => entry.item.route === activeRoute)?.item.route ?? false;

  return (
    <Paper
      elevation={8}
      sx={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: (theme) => theme.zIndex.appBar, pb: "env(safe-area-inset-bottom)" }}
    >
      <BottomNavigation
        showLabels
        value={current}
        onChange={(_e, value: string) => (value === "more" ? onMore() : navigate(value))}
        sx={{ height: BOTTOM_NAV_HEIGHT, "& .MuiBottomNavigationAction-root": { minWidth: 0, px: 0.5 }, "& .MuiBottomNavigationAction-label": { fontSize: 11 } }}
      >
        <BottomNavigationAction label="Home" value="/app/dashboard" icon={<DashboardIcon />} />
        {items.map(({ item, icon }) => {
          const Icon = menuIconMap[icon];
          return <BottomNavigationAction key={item.route} label={SHORT_LABELS[item.route] ?? item.label} value={item.route} icon={<Icon />} />;
        })}
        <BottomNavigationAction label="More" value="more" icon={<MenuIcon />} />
      </BottomNavigation>
    </Paper>
  );
};
